import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, useAuth } from '../../App';
import AdminLayout from '../../components/AdminLayout';
import { toast } from 'sonner';
import { 
  Plus,
  Calendar,
  Loader2,
  Search,
  Trash2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';

const lotteryLocalDate = (lottery) => {
  const timeZone = lottery?.company === 'Americanas' ? 'America/New_York' : 'America/Santo_Domingo';
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(new Date());
  const values = Object.fromEntries(parts.map(({ type, value }) => [type, value]));
  return `${values.year}-${values.month}-${values.day}`;
};

const AdminResults = () => {
  const { token } = useAuth();
  const [results, setResults] = useState([]);
  const [lotteries, setLotteries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedLottery, setSelectedLottery] = useState('all');
  const [formData, setFormData] = useState({
    lottery_id: '',
    lottery_code: '',
    draw_date: new Date().toISOString().split('T')[0],
    draw_time: '',
    numbers: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [resultsRes, lotteriesRes] = await Promise.all([
        axios.get(`${API}/results?limit=100`),
        axios.get(`${API}/lotteries`)
      ]);
      setResults(resultsRes.data);
      setLotteries(lotteriesRes.data);
    } catch (e) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setFormData({
      lottery_id: lotteries[0]?.id || '',
      lottery_code: lotteries[0]?.code || '',
      draw_date: lotteryLocalDate(lotteries[0]),
      draw_time: lotteries[0]?.draw_time || '',
      numbers: ''
    });
    setDialogOpen(true);
  };

  const handleLotteryChange = (lotteryId) => {
    const lottery = lotteries.find(l => l.id === lotteryId);
    const previous = lotteries.find(l => l.id === formData.lottery_id);
    setFormData({
      ...formData,
      lottery_id: lotteryId,
      lottery_code: lottery?.code || '',
      draw_date: formData.draw_date === lotteryLocalDate(previous) ? lotteryLocalDate(lottery) : formData.draw_date,
      draw_time: lottery?.draw_time || ''
    });
  };

  const handleSubmit = async () => {
    try {
      const numbersArray = formData.numbers.split(',').map(n => parseInt(n.trim())).filter(n => !isNaN(n));
      
      if (numbersArray.length === 0) {
        toast.error('Ingresa números válidos separados por coma');
        return;
      }

      await axios.post(`${API}/results`, {
        ...formData,
        numbers: numbersArray
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Resultado agregado');
      setDialogOpen(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar resultado');
    }
  };

  const filteredResults = selectedLottery === 'all' 
    ? results 
    : results.filter(r => r.lottery_id === selectedLottery);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    // Parse YYYY-MM-DD without timezone conversion
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      const d = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
      return d.toLocaleDateString('es-DO', { day: '2-digit', month: 'short', year: 'numeric' });
    }
    return dateStr;
  };

  if (loading) {
    return (
      <AdminLayout title="Gestión de Resultados" subtitle="Cargando...">
        <div className="flex items-center justify-center h-64">
          <Loader2 size={40} className="text-amber-400 animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Gestión de Resultados" subtitle={`${results.length} resultados registrados`}>
      {/* Filters & Actions */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <Select value={selectedLottery} onValueChange={setSelectedLottery}>
          <SelectTrigger className="w-[250px] bg-gray-900 border-gray-700 text-white">
            <SelectValue placeholder="Filtrar por lotería" />
          </SelectTrigger>
          <SelectContent className="bg-gray-900 border-gray-700">
            <SelectItem value="all" className="text-white hover:bg-gray-800">Todas las loterías</SelectItem>
            {lotteries.map((lottery) => (
              <SelectItem key={lottery.id} value={lottery.id} className="text-white hover:bg-gray-800">
                {lottery.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <div className="flex-1" />
        
        <Button onClick={openCreateDialog} className="bg-amber-500 hover:bg-amber-400 text-black">
          <Plus size={18} className="mr-2" />
          Agregar Resultado
        </Button>
      </div>

      {/* Results Table */}
      <Card className="bg-gray-900 border-gray-800">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-gray-800 hover:bg-transparent">
                <TableHead className="text-gray-400">Lotería</TableHead>
                <TableHead className="text-gray-400">Fecha</TableHead>
                <TableHead className="text-gray-400">Hora</TableHead>
                <TableHead className="text-gray-400">Números</TableHead>
                <TableHead className="text-gray-400">Fuente</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredResults.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-gray-500 py-8">
                    No hay resultados que mostrar
                  </TableCell>
                </TableRow>
              ) : (
                filteredResults.slice(0, 50).map((result) => {
                  const lottery = lotteries.find(l => l.id === result.lottery_id);
                  return (
                    <TableRow key={result.id} className="border-gray-800 hover:bg-gray-800/50">
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-2 h-2 rounded-full" 
                            style={{ backgroundColor: lottery?.color || '#22c55e' }}
                          />
                          <span className="text-white">{lottery?.name || result.lottery_code}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-gray-400">
                        {formatDate(result.draw_date)}
                      </TableCell>
                      <TableCell className="text-gray-400">
                        {result.draw_time || '-'}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {result.numbers?.map((num, i) => (
                            <span 
                              key={i}
                              className="w-8 h-8 rounded-full bg-green-500/20 text-green-400 flex items-center justify-center text-sm font-bold"
                            >
                              {num}
                            </span>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={result.source === 'scrape' ? 'bg-blue-500/20 text-blue-400' : 'bg-purple-500/20 text-purple-400'}>
                          {result.source === 'scrape' ? 'Auto' : 'Manual'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md bg-gray-900 border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-white">Agregar Resultado</DialogTitle>
            <DialogDescription className="text-gray-400">
              Ingresa manualmente un resultado de sorteo
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-gray-300">Lotería</Label>
              <Select value={formData.lottery_id} onValueChange={handleLotteryChange}>
                <SelectTrigger className="w-full bg-gray-800 border-gray-700 text-white">
                  <SelectValue placeholder="Selecciona lotería" />
                </SelectTrigger>
                <SelectContent className="bg-gray-900 border-gray-700">
                  {lotteries.map((lottery) => (
                    <SelectItem key={lottery.id} value={lottery.id} className="text-white hover:bg-gray-800">
                      {lottery.name} ({lottery.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Fecha</Label>
                <Input
                  type="date"
                  value={formData.draw_date}
                  onChange={(e) => setFormData({...formData, draw_date: e.target.value})}
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Hora</Label>
                <Input
                  value={formData.draw_time}
                  readOnly
                  aria-label="Horario registrado del sorteo"
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">Números (separados por coma)</Label>
              <Input
                value={formData.numbers}
                onChange={(e) => setFormData({...formData, numbers: e.target.value})}
                placeholder="12, 45, 78"
                className="bg-gray-800 border-gray-700 text-white font-mono"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)} className="text-gray-400">
              Cancelar
            </Button>
            <Button onClick={handleSubmit} className="bg-amber-500 hover:bg-amber-400 text-black">
              Guardar Resultado
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminResults;
